import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Decimal from 'decimal.js';
import styled from '@emotion/styled';
import PointsPaySlider, { usePointsPaySlider } from 'components/PointsPaySlider';
import { Box, Flex, Input } from 'roo-ui/components';
import usePointsConverters from 'hooks/points/usePointsConverters';
import formatNumber from 'lib/formatters/formatNumber';
import { FieldLabel } from 'components/Form';
import useSliderKey from 'pages/Checkout/CustomerDetails/Steps/PaymentOptions/hooks/useSliderKey';

const BoldInput = styled(Input)`
  font-weight: bold;
`;

const adjustCashAmountWithinRange = ({ cashAmount, minCashAmount, maxCashAmount }) => {
  return Decimal.min(Decimal.max(cashAmount, minCashAmount), maxCashAmount);
};

const formattedStringToNumber = (formatted) => (formatted ? Number(formatted.replace(/(,|\$)/g, '')) : 0);
const maskPoints = (cash) => cash.replace(/[^0-9,]/g, '');
const maskCash = (cash) => cash.replace(/[^0-9.,$]/g, '');
const formatPoints = (pointsAmount) => formatNumber({ number: pointsAmount.toNumber(), decimalPlaces: 0 });
const formatCash = (cashAmount) => `$${formatNumber({ number: cashAmount.toNumber(), decimalPlaces: 2 })}`;

const FullPointsPaySlider = ({ totalCashAmount, initialPointsAmount, updatePointsAndCash }) => {
  const { convertPointsToCash } = usePointsConverters();

  const { pointsAmount, pointsAmountInCash, cashAmount, minCashAmount, maxCashAmount, initialCashAmount, onChange } = usePointsPaySlider({
    totalCash: { amount: totalCashAmount },
    initialPointsAmount,
  });

  const [bufferedPointsAmount, setBufferedPointsAmount] = useState(pointsAmount);
  const [bufferedCashAmount, setBufferedCashAmount] = useState(cashAmount);
  const { sliderKey, regenerateSliderKey } = useSliderKey();

  useEffect(() => {
    updatePointsAndCash({
      points: {
        amount: pointsAmount,
        amountInCash: pointsAmountInCash,
      },
      cash: {
        amount: cashAmount,
      },
    });
    setBufferedPointsAmount(formatPoints(pointsAmount));
    setBufferedCashAmount(formatCash(cashAmount));
  }, [pointsAmount.toNumber(), pointsAmountInCash.toNumber(), cashAmount.toNumber(), updatePointsAndCash]); // eslint-disable-line react-hooks/exhaustive-deps

  const updatePointsAmount = () => {
    const pointsAmount = bufferedPointsAmount ? new Decimal(formattedStringToNumber(bufferedPointsAmount)) : new Decimal(0);
    let cashAmount = totalCashAmount.minus(convertPointsToCash({ points: pointsAmount }));
    cashAmount = adjustCashAmountWithinRange({ cashAmount, minCashAmount, maxCashAmount });
    onChange(cashAmount);
    regenerateSliderKey();
  };

  const updateCashAmount = () => {
    let cashAmount = bufferedCashAmount ? new Decimal(formattedStringToNumber(bufferedCashAmount)) : new Decimal(0);
    cashAmount = adjustCashAmountWithinRange({ cashAmount, minCashAmount, maxCashAmount });
    onChange(cashAmount);
    regenerateSliderKey();
  };

  return (
    <Box>
      <PointsPaySlider
        key={sliderKey}
        minCashAmount={minCashAmount.toNumber()}
        maxCashAmount={maxCashAmount.toNumber()}
        initialCashAmount={initialCashAmount.toNumber()}
        onChange={onChange}
      />
      <Flex justifyContent="space-between" mt={4}>
        <Box width="130px">
          <FieldLabel htmlFor="points">
            Qantas Points
            <BoldInput
              id="points"
              name="points"
              value={bufferedPointsAmount}
              onChange={(e) => setBufferedPointsAmount(maskPoints(e.target.value))}
              onBlur={updatePointsAmount}
              onKeyUp={(e) => e.keyCode === 13 && updatePointsAmount()}
              data-testid="points-input"
            />
          </FieldLabel>
        </Box>
        <Box textAlign="right" width="130px">
          <FieldLabel htmlFor="cash">
            Cash (AUD)
            <BoldInput
              id="cash"
              name="cash"
              value={bufferedCashAmount}
              onChange={(e) => setBufferedCashAmount(maskCash(e.target.value))}
              onBlur={updateCashAmount}
              onKeyUp={(e) => e.keyCode === 13 && updateCashAmount()}
              data-testid="cash-input"
              textAlign="right"
            />
          </FieldLabel>
        </Box>
      </Flex>
    </Box>
  );
};

FullPointsPaySlider.propTypes = {
  totalCashAmount: PropTypes.instanceOf(Decimal).isRequired,
  initialPointsAmount: PropTypes.instanceOf(Decimal),
  updatePointsAndCash: PropTypes.func.isRequired,
};

FullPointsPaySlider.defaultProps = {
  initialPointsAmount: new Decimal(0),
};

export default FullPointsPaySlider;
